import { createSelector } from '@reduxjs/toolkit';
import { getConfiguration } from '../appSettings/app.settings.state';
import { getCodeTableDictionary } from '../codeTables/codeTables.state';
import { sortAlphabetically } from '../helpers/array.helpers';
import { CodeTableNumber, LabelValuePair } from '../interfaces';
import { UserLanguagePreferenceSelector } from '../security/session.state';

const DEFAULT_DISPLAY_LANGUAGE = 'en-US';
const EMPTY_ARRAY: LabelValuePair<string>[] = [];

export const UserLanguagesForCurrentBusinessUnitSelector = createSelector(
  [
    getCodeTableDictionary(CodeTableNumber.BusinessUnitLanguageConfiguration),
    getCodeTableDictionary(CodeTableNumber.Language)
  ],
  (businessUnitLanguages, metadataLanguages) => {
    const businessUnitLanguagesArray = Object.values(businessUnitLanguages);

    if (
      businessUnitLanguagesArray?.length &&
      Object.values(metadataLanguages)?.length
    ) {
      return sortAlphabetically(
        businessUnitLanguagesArray.map((language) => {
          const value = language.AdditionalProperties.language;
          return {
            label: metadataLanguages[value].Name || '',
            value
          } as LabelValuePair<string>;
        }),
        'label'
      );
    }
    return EMPTY_ARRAY;
  }
);

export const UserDisplayLanguagesForCurrentBusinessUnitSelector =
  createSelector(
    [
      UserLanguagesForCurrentBusinessUnitSelector,
      getConfiguration,
      getCodeTableDictionary(CodeTableNumber.Language)
    ],
    (userLanguages, configuration, metadataLanguages) => {
      const userLanguagesConfigured = structuredClone(userLanguages);
      const hasEnUsLang = userLanguages.find((buLang) => {
        return buLang.value === DEFAULT_DISPLAY_LANGUAGE;
      });
      if (!hasEnUsLang && metadataLanguages[DEFAULT_DISPLAY_LANGUAGE]) {
        userLanguagesConfigured.push({
          label: metadataLanguages?.[DEFAULT_DISPLAY_LANGUAGE]?.Name || '',
          value: metadataLanguages?.[DEFAULT_DISPLAY_LANGUAGE]?.Value || ''
        });
      }

      return sortAlphabetically(
        userLanguagesConfigured.filter((item) =>
          [...configuration.languages, DEFAULT_DISPLAY_LANGUAGE].includes(
            item.value
          )
        ),
        'label'
      );
    }
  );

export const UserLanguagePreferenceWithLabelSelector = createSelector(
  [UserLanguagePreferenceSelector, UserLanguagesForCurrentBusinessUnitSelector],
  (userLanguagePreference, userLanguagesForCurrentBusinessUnit) => {
    return userLanguagesForCurrentBusinessUnit.find((userLanguage) => {
      return userLanguage.value === userLanguagePreference;
    });
  }
);

export const SupportedLanguageCodesSelector = createSelector(
  [getCodeTableDictionary(CodeTableNumber.BusinessUnitLanguageConfiguration)],
  (businessUnitLanguages): string[] => {
    return Object.values(businessUnitLanguages).map(
      (businessUnitLanguage) =>
        businessUnitLanguage.AdditionalProperties.language
    );
  }
);
